// handleincomingmessage.js

import MessageHeaderDecoder from '../aeron/MessageHeaderDecoder.js';
import QuoteRequestDecoder from '../aeron/v1/QuoteRequestDecoder.js';
import QuoteDecoder from '../aeron/v1/QuoteDecoder.js';
import QuoteCancelDecoder from '../aeron/v1/QuoteCancelDecoder.js';
import DealRequestDecoder from '../aeron/v1/DealRequestDecoder.js';
import ExecutionReportDecoder from '../aeron/v1/ExecutionReportDecoder.js';
import ErrorDecoder from '../aeron/v1/ErrorDecoder.js';

import TradeQuoteRequestDecoder from '../aeron/v2/TradeQuoteRequestDecoder.js';
import TradeQuoteCancelDecoder from '../aeron/v2/TradeQuoteCancelDecoder.js';
import TradeQuoteDecoder from '../aeron/v2/TradeQuoteDecoder.js';
import TradeDealRequestDecoder from '../aeron/v2/TradeDealRequestDecoder.js';
import TradeExecutionReportDecoder from '../aeron/v2/TradeExecutionReportDecoder.js';
import TradeErrorDecoder from '../aeron/v2/TradeErrorDecoder.js';

import AdminDecoder from '../aeron/admin/AdminDecoder.js';

const handleIncomingMessage = (data, setMessage, metadata = null) => {
    if (!(data instanceof ArrayBuffer)) {
        console.error('Data is not an ArrayBuffer:', data);
        return;
    }

    try {
        const headerDecoder = new MessageHeaderDecoder();
        headerDecoder.wrap(data, 0);

        console.log('Received message header:', headerDecoder.toString());

        const schemaId = headerDecoder.schemaId();
        const templateId = headerDecoder.templateId();

        let decodedData;
        try {
            switch (schemaId) {
                case 1: // Messages schema
                    decodedData = decodeMessagesSchema(templateId, data);
                    break;
                case 3: // Admin schema
                    decodedData = decodeAdminSchema(templateId, data);
                    break;
                case 4: // v2 messages schema
                    decodedData = decodeV2MessagesSchema(templateId, data);
                    break;
                default:
                    console.error('Unknown schemaId:', schemaId);
                    return;
            }
        } catch (err) {
            console.error(`Error decoding message: schemaId=${schemaId}, templateId=${templateId}`, err);
            return;
        }

        if (decodedData) {
            setMessage(applyCaptureMetadata(decodedData, metadata));
        }

    } catch (error) {
        console.error('Error processing message:', error);
    }
};

export default handleIncomingMessage;

const decodeMessagesSchema = (templateId, data) => {
    switch (templateId) {
        case 1: { // DealRequest
            const decoder = new DealRequestDecoder();
            decoder.wrap(data, MessageHeaderDecoder.ENCODED_LENGTH);
            logData(data);
            return {
                type: "dealRequest",
                amount: decoder.decodeamount(),
                currency: decoder.currency(),
                side: trimNulls(decoder.side()),
                symbol: trimNulls(decoder.symbol()),
                deliveryDate: decoder.deliveryDate(),
                transactTime: trimNulls(decoder.transactTime()),
                quoteRequestID: trimNulls(decoder.quoteRequestID()),
                quoteID: trimNulls(decoder.quoteID()),
                dealRequestID: trimNulls(decoder.dealRequestID()),
                fxRate: decoder.decodefxRate(),
                secondaryAmount: decoder.decodesecondaryAmount(),
                clientID: trimNulls(decoder.clientID())
            };
        }
        case 2: { // Execution Report
            const decoder = new ExecutionReportDecoder();
            decoder.wrap(data, MessageHeaderDecoder.ENCODED_LENGTH);
            logData(data);
            return {
                type: "executionReport",
                amount: decoder.decodeamount(),
                currency: trimNulls(decoder.currency()),
                secondaryAmount: decoder.decodesecondaryAmount(),
                secondaryCurrency: decoder.secondaryCurrency(),
                side: trimNulls(decoder.side()),
                symbol: trimNulls(decoder.symbol()),
                deliveryDate: decoder.deliveryDate(),
                transactTime: trimNulls(decoder.transactTime()),
                quoteRequestID: trimNulls(decoder.quoteRequestID()),
                quoteID: trimNulls(decoder.quoteID()),
                dealRequestID: trimNulls(decoder.dealRequestID()),
                dealID: trimNulls(decoder.dealID()),
                fxRate: decoder.decodefxRate(),
                clientID: trimNulls(decoder.clientID())
            };
        }

        case 3: { // QuoteRequest
            const decoder = new QuoteRequestDecoder();
            decoder.wrap(data, MessageHeaderDecoder.ENCODED_LENGTH);
            logData(data);
            return {
                type: "quoteRequest",
                amount: decoder.decodeamount(),
                saleCurrency: decoder.saleCurrency(),
                side: trimNulls(decoder.side()),
                symbol: trimNulls(decoder.symbol()),
                deliveryDate: decoder.deliveryDate(),
                transactTime: trimNulls(decoder.transactTime()),
                quoteRequestID:trimNulls( decoder.quoteRequestID()),
                currencyOwned: decoder.currencyOwned(),
                clientID: trimNulls(decoder.clientID())
            };
        }

        case 4: { // Quote
            const decoder = new QuoteDecoder();
            decoder.wrap(data, MessageHeaderDecoder.ENCODED_LENGTH);
            logData(data);
            return  {
                type: "quote",
                amount: decoder.decodeamount(),
                currency: decoder.currency(),
                side: trimNulls(decoder.side()),
                symbol: trimNulls(decoder.symbol()),
                transactTime: trimNulls(decoder.transactTime()),
                quoteID: trimNulls(decoder.quoteID()),
                quoteRequestID: trimNulls(decoder.quoteRequestID()),
                fxRate: decoder.decodefxRate(),
                secondaryAmount: decoder.decodesecondaryAmount(),
                clientID: trimNulls(decoder.clientID())
            };
        }

        case 5: { // QuoteCancel
            const decoder = new QuoteCancelDecoder();
            decoder.wrap(data, MessageHeaderDecoder.ENCODED_LENGTH);
            logData(data);
            return {
                type: "quoteCancel",
                symbol: trimNulls(decoder.symbol()),
                transactTime: trimNulls(decoder.transactTime()),
                quoteRequestID:trimNulls(decoder.quoteRequestID()),
                clientID: trimNulls(decoder.clientID())
            };
        }

        case 6: { // Error
            const decoder = new ErrorDecoder();
            decoder.wrap(data, MessageHeaderDecoder.ENCODED_LENGTH);
            logData(data);
            return {
                type: "error",
                amount: decoder.decodeamount(),
                currency: decoder.currency(),
                side: trimNulls(decoder.side()),
                symbol: trimNulls(decoder.symbol()),
                deliveryDate: decoder.deliveryDate(),
                transactTime: trimNulls(decoder.transactTime()),
                quoteRequestID: trimNulls(decoder.quoteRequestID()),
                quoteID: trimNulls(decoder.quoteID()),
                dealRequestID: trimNulls(decoder.dealRequestID()),
                dealID: trimNulls(decoder.dealID()),
                fxRate: decoder.decodefxRate(),
                clientID: trimNulls(decoder.clientID()),
                message: trimNulls(decoder.message())
            };
        }

        default:
            console.error('Unknown message templateId:', templateId);
            return null;
    }
};

const decodeAdminSchema = (templateId, data) => {
    if (templateId === 1) { // Admin message
        const decoder = new AdminDecoder();
        decoder.wrap(data, MessageHeaderDecoder.ENCODED_LENGTH);
        console.log('Admin');
        logData(data);
        return {
            type: "admin",
            applicationName: trimNulls(decoder.applicationName()),
            instanceId: trimNulls(decoder.instanceId()),
            environment: trimNulls(decoder.environment()),
            messageType: trimNulls(decoder.messageType()),
            timestamp: decoder.timestamp(),
            detailedMessage: trimNulls(decoder.detailedMessage()),
            hostInfo: trimNulls(decoder.hostInfo())
        };
    } else {
        console.error('Unknown Admin templateId:', templateId);
        return null;
    }
};

const decodeV2MessagesSchema = (templateId, data) => {
    switch (templateId) {
        case 1: { // TradeQuoteRequest
            console.log('TradeQuoteRequest');
            const decoder = new TradeQuoteRequestDecoder();
            decoder.wrap(data, MessageHeaderDecoder.ENCODED_LENGTH);

            logData(data);
            return {
                type: "tradeQuoteRequest",
                transactionType: trimNulls(decoder.transactionType()),
                symbol: trimNulls(decoder.symbol()),
                transactTime: trimNulls(decoder.transactTime()),
                messageTime: decoder.messageTime(),
                quoteRequestID: trimNulls(decoder.quoteRequestID()),
                clientID: trimNulls(decoder.clientID()),
                legs: decodeLegs(decoder.decodeLeg())
            };
        }
        case 2: { // TradeQuote
            console.log('TradeQuote');
            const decoder = new TradeQuoteDecoder();
            decoder.wrap(data, MessageHeaderDecoder.ENCODED_LENGTH);

            logData(data);
            return {
                type: "tradeQuote",
                transactionType: trimNulls(decoder.transactionType()),
                symbol: trimNulls(decoder.symbol()),
                transactTime: trimNulls(decoder.transactTime()),
                messageTime: decoder.messageTime(),
                quoteRequestID: trimNulls(decoder.quoteRequestID()),
                quoteID: trimNulls(decoder.quoteID()),
                clientID: trimNulls(decoder.clientID()),
                legs: decodeLegs(decoder.decodeLeg())
            };
        }
        case 3: { // TradeDealRequest
            console.log('TradeDealRequest');
            const decoder = new TradeDealRequestDecoder();
            decoder.wrap(data, MessageHeaderDecoder.ENCODED_LENGTH);

            logData(data);
            return {
                type: "tradeDealRequest",
                transactionType: trimNulls(decoder.transactionType()),
                symbol: trimNulls(decoder.symbol()),
                transactTime: trimNulls(decoder.transactTime()),
                messageTime: decoder.messageTime(),
                quoteRequestID: trimNulls(decoder.quoteRequestID()),
                quoteID: trimNulls(decoder.quoteID()),
                dealRequestID: trimNulls(decoder.dealRequestID()),
                clientID: trimNulls(decoder.clientID()),
                legs: decodeLegs(decoder.decodeLeg())
            };
        }
        case 4: { // TradeExecutionReport
            console.log('TradeExecutionReport');
            const decoder = new TradeExecutionReportDecoder();
            decoder.wrap(data, MessageHeaderDecoder.ENCODED_LENGTH);

            logData(data);
            return {
                type: "tradeExecutionReport",
                transactionType: trimNulls(decoder.transactionType()),
                symbol: trimNulls(decoder.symbol()),
                transactTime: trimNulls(decoder.transactTime()),
                messageTime: decoder.messageTime(),
                quoteRequestID: trimNulls(decoder.quoteRequestID()),
                quoteID: trimNulls(decoder.quoteID()),
                dealRequestID: trimNulls(decoder.dealRequestID()),
                dealID: trimNulls(decoder.dealID()),
                clientID: trimNulls(decoder.clientID()),
                legs: decodeLegs(decoder.decodeLeg())
            };
        }
        case 5: { // TradeError
            console.log('TradeError');
            const decoder = new TradeErrorDecoder();
            decoder.wrap(data, MessageHeaderDecoder.ENCODED_LENGTH);

            logData(data);
            return {
                type: "tradeError",
                transactionType: trimNulls(decoder.transactionType()),
                symbol: trimNulls(decoder.symbol()),
                transactTime: trimNulls(decoder.transactTime()),
                messageTime: decoder.messageTime(),
                quoteRequestID: trimNulls(decoder.quoteRequestID()),
                quoteID: trimNulls(decoder.quoteID()),
                dealRequestID: trimNulls(decoder.dealRequestID()),
                dealID: trimNulls(decoder.dealID()),
                clientID: trimNulls(decoder.clientID()),
                legs: decodeLegs(decoder.decodeLeg()),
                message: trimNulls(decoder.message())
            };
        }
        case 6: { // TradeQuoteCancel
            console.log('TradeQuoteCancel');
            const decoder = new TradeQuoteCancelDecoder();
            decoder.wrap(data, MessageHeaderDecoder.ENCODED_LENGTH);

            logData(data);
            return {
                type: "tradeQuoteCancel",
                transactionType: trimNulls(decoder.transactionType()),
                symbol: trimNulls(decoder.symbol()),
                transactTime: trimNulls(decoder.transactTime()),
                messageTime: decoder.messageTime(),
                quoteRequestID: trimNulls(decoder.quoteRequestID()),
                clientID: trimNulls(decoder.clientID())
            };
        }

        default:
            console.error('Unknown message templateId:', templateId);
            return null;
    }
}

function logData(data) {
    // 🧪 LOG: check alignment after header
    const startOffset = MessageHeaderDecoder.ENCODED_LENGTH;
    const byteSlice = new Uint8Array(data, startOffset);
    const hexDump = Array.from(byteSlice).map(b => b.toString(16).padStart(2, '0')).join(' ');
    console.log('🧪 Bytes after header:', hexDump);
}

function trimNulls(value) {
    return value.replace(/\0/g, '');
}

function decodeLegs(legs) {
    return legs.map(leg => ({
        ...leg,
        currency: trimNulls(leg.currency),
        side: trimNulls(leg.side),
    }));
}

function applyCaptureMetadata(decodedData, metadata) {
    if (!metadata || typeof metadata !== 'object') {
        return decodedData;
    }

    const captureMetadata = {};

    if (metadata.channelName) {
        captureMetadata.channelName = metadata.channelName;
    }
    if (metadata.sourceChannel) {
        captureMetadata.sourceChannel = metadata.sourceChannel;
    }
    if (metadata.streamId !== undefined && metadata.streamId !== null) {
        captureMetadata.streamId = metadata.streamId;
    }
    if (metadata.sessionId !== undefined && metadata.sessionId !== null) {
        captureMetadata.sessionId = metadata.sessionId;
    }
    if (metadata.sourcePosition !== undefined && metadata.sourcePosition !== null) {
        captureMetadata.sourcePosition = metadata.sourcePosition;
    }
    if (metadata.ingestSource) {
        captureMetadata.ingestSource = metadata.ingestSource;
    }
    if (metadata.ingestTimestamp !== undefined && metadata.ingestTimestamp !== null) {
        captureMetadata.ingestTimestamp = metadata.ingestTimestamp;
    }

    return {
        ...decodedData,
        ...captureMetadata,
    };
}
